// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/ParticipantCardButtons/SigningMethodTypeButton/index.tsx

import { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { faComment, faEnvelope, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Text } from '@modusign/design-system';
import { SigningMethod } from '@modusign/react-signing/types';
import { SystemCssProperties } from '@styled-system/css';

interface SigningMethodTypeButtonProps {
  isSelectBlocked: boolean;
  type: SigningMethod;
  onClick: () => void;
}

export function SigningMethodTypeButton({
  isSelectBlocked,
  type,
  onClick,
}: SigningMethodTypeButtonProps) {
  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  }

  const commonStyle: SystemCssProperties = { flex: 1 };
  const emailButtonStyle: SystemCssProperties = {
    ...commonStyle,
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
  };
  const phoneButtonStyle: SystemCssProperties = {
    ...commonStyle,
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
  };

  const selectBlockedStyle: SystemCssProperties = {
    border: 'buttonBorderNeutralBase',
    background: 'buttonBackgroundNeutralBase',
    color: 'textNeutralUltraLight',
  };

  switch (type) {
    case 'EMAIL':
      return (
        <Button
          display="flex"
          fontSize={1}
          px={0}
          sx={
            isSelectBlocked
              ? {
                  ...emailButtonStyle,
                  ...selectBlockedStyle,
                }
              : emailButtonStyle
          }
          weight="light"
          onClick={handleClick}
        >
          <SigningMethodButtonContents
            icon={faEnvelope}
            isSelectBlocked={isSelectBlocked}
            text="이메일"
          />
        </Button>
      );
    case 'KAKAO':
      return (
        <Button
          display="flex"
          fontSize={1}
          /**
           * 카카오톡은 이메일과 항상 함께 렌더링 된다는 가정이 있고,
           * 이메일 버튼과 붙어있는 레이아웃을 위해 marginLeft 수정
           */
          ml="-2px"
          px={0}
          sx={
            isSelectBlocked
              ? {
                  ...phoneButtonStyle,
                  ...selectBlockedStyle,
                }
              : phoneButtonStyle
          }
          weight="light"
          onClick={handleClick}
        >
          <SigningMethodButtonContents
            icon={faComment}
            isSelectBlocked={isSelectBlocked}
            text="카카오톡"
          />
        </Button>
      );
    case 'IN_PERSON': {
      return (
        <Button
          display="flex"
          fontSize={1}
          /**
           * 대면서명은 이메일, 카카오톡과 떨어져서 렌더링한다는 가정이 있어서
           * marginLeft 수정
           */
          ml={3}
          px={0}
          sx={
            isSelectBlocked
              ? {
                  ...commonStyle,
                  ...selectBlockedStyle,
                }
              : commonStyle
          }
          weight="light"
          onClick={handleClick}
        >
          <SigningMethodButtonContents
            icon={faUser}
            isSelectBlocked={isSelectBlocked}
            text="대면서명"
          />
        </Button>
      );
    }

    default:
      return null;
  }
}

function SigningMethodButtonContents({
  icon,
  isSelectBlocked,
  text,
}: {
  icon: IconLookup;
  isSelectBlocked: boolean;
  text: string;
}) {
  return (
    <>
      <Text
        color={isSelectBlocked ? 'textNeutralUltraLight' : 'iconNeutralSlightlyDark'}
        fontSize="sm"
        mr={2}
      >
        <FontAwesomeIcon icon={icon} />
      </Text>
      <Text
        color={isSelectBlocked ? 'textNeutralUltraLight' : 'textNeutralSlightlyLight'}
        fontSize="sm"
      >
        {text}
      </Text>
    </>
  );
}
